import { GROWN_MAX_CATEGORIES } from '@klopotun/domain';
import { z } from 'zod';
import { amountMinorSchema, civilDateSchema, convertedAmountMinorSchema, currencyCodeSchema, uuidSchema, } from './common.js';
import { paymentKindSchema } from './payment.js';
const MONTHS_PER_YEAR = 12;
/**
 * A query string carries the year as text. Four digits and nothing else: the calendar rule in
 * packages/domain refuses a year zero, and Postgres refuses it too, so a shorter or a signed form
 * would only reach a 500 a few layers down.
 */
export const yearQueryParamSchema = z
    .string()
    .regex(/^\d{4}$/, 'must be a four digit year')
    .transform(Number);
/** No year is not "every year": the server answers with the year of the person's own today. */
export const summaryQuerySchema = z.object({
    year: yearQueryParamSchema.optional(),
});
export const NO_CATEGORY_FILTER = 'none';
export const monthQueryParamSchema = z
    .string()
    .regex(/^(?:[1-9]|1[0-2])$/, 'must be a month from 1 to 12')
    .transform(Number);
export const yearSummaryQuerySchema = summaryQuerySchema.extend({
    month: monthQueryParamSchema.optional(),
    category: z.union([uuidSchema, z.literal(NO_CATEGORY_FILTER)]).optional(),
});
const totalMinorSchema = z.int().min(0);
const spendingMonthSchema = z.object({
    month: z.int().min(1).max(MONTHS_PER_YEAR),
    exactMinor: totalMinorSchema,
    estimatedMinor: totalMinorSchema,
});
const categorySpendingSchema = z.object({
    /** Empty for the payments that have no category. */
    categoryId: uuidSchema.nullable(),
    annualCostMinor: totalMinorSchema,
    exactMinor: totalMinorSchema,
    estimatedMinor: totalMinorSchema,
    bankMinor: totalMinorSchema,
});
const merchantSpendingSchema = z.object({
    categoryId: uuidSchema.nullable(),
    merchantKey: z.string().min(1),
    name: z.string().min(1),
    spentMinor: totalMinorSchema,
    operationCount: z.int().min(1),
});
const incomeMonthSchema = z.object({
    month: z.int().min(1).max(MONTHS_PER_YEAR),
    amountMinor: totalMinorSchema,
});
const summaryBankSchema = z.object({
    isHistoryComplete: z.boolean(),
    convertedOperationCount: z.int().min(0),
    unconvertedOperationCount: z.int().min(0),
});
const paymentSpendingSchema = z.object({
    paymentId: uuidSchema,
    name: z.string().min(1),
    categoryId: uuidSchema.nullable(),
    annualCostMinor: totalMinorSchema,
    exactMinor: totalMinorSchema,
    estimatedMinor: totalMinorSchema,
    isConverted: z.boolean(),
});
/**
 * The two figures of design note C5a, never added together: the hard annual cost of the fixed
 * payments, and what the calendar year comes to - facts for what was paid, planned periods of
 * the fixed payments, and estimates for the variable ones, the estimates always told apart.
 */
export const yearSummarySchema = z.object({
    year: z.int(),
    month: z.int().min(1).max(MONTHS_PER_YEAR).nullable(),
    baseCurrency: currencyCodeSchema,
    today: civilDateSchema,
    rateDate: civilDateSchema.nullable(),
    annualCost: z.object({
        totalMinor: totalMinorSchema,
        /** At least one payment is in another currency, so the total is approximate. */
        isConverted: z.boolean(),
        /** At least one variable payment is counted by its average over the last twelve months. */
        hasEstimates: z.boolean(),
        paymentCount: z.int().min(0),
    }),
    spending: z.object({
        exactMinor: totalMinorSchema,
        estimatedMinor: totalMinorSchema,
        totalMinor: totalMinorSchema,
        spentMinor: totalMinorSchema,
        plannedMinor: totalMinorSchema,
        hasEstimates: z.boolean(),
        months: z.array(spendingMonthSchema).length(MONTHS_PER_YEAR),
    }),
    subscriptions: z.object({
        exactMinor: totalMinorSchema,
        estimatedMinor: totalMinorSchema,
        isConverted: z.boolean(),
    }),
    income: z.object({
        totalMinor: totalMinorSchema,
        isConverted: z.boolean(),
        months: z.array(incomeMonthSchema).length(MONTHS_PER_YEAR),
    }),
    categories: z.array(categorySpendingSchema),
    payments: z.array(paymentSpendingSchema),
    merchants: z.array(merchantSpendingSchema),
    /** Variable payments with no fact to extrapolate from - out of the totals, not out of sight. */
    withoutForecast: z.array(z.object({ paymentId: uuidSchema, name: z.string().min(1) })),
    /** Currencies the rates of the day did not carry; the payments in them were left out. */
    missingRates: z.array(currencyCodeSchema),
    /** Confirmed periods without a converted amount, left out of the facts. */
    unconvertedPaidCount: z.int().min(0),
    bank: summaryBankSchema.nullable(),
});
const grownCategorySchema = z.object({
    categoryId: uuidSchema,
    name: z.string().min(1),
    emoji: z.string().min(1).nullable(),
    color: z.string().min(1).nullable(),
    spentMinor: totalMinorSchema,
    usualMinor: totalMinorSchema,
});
export const monthInsightSchema = z.object({
    year: z.int(),
    month: z.int().min(1).max(MONTHS_PER_YEAR),
    today: civilDateSchema,
    baseCurrency: currencyCodeSchema,
    spentMinor: totalMinorSchema,
    usualMinor: totalMinorSchema.nullable(),
    forecastMinor: totalMinorSchema,
    daysLeft: z.int().min(0),
    grown: z.array(grownCategorySchema).max(GROWN_MAX_CATEGORIES),
    isSpentApproximate: z.boolean(),
    isForecastApproximate: z.boolean(),
    isHistoryComplete: z.boolean(),
});
export const calendarEntryStateSchema = z.enum(['paid', 'planned', 'forecast']);
export const calendarEntrySchema = z.object({
    paymentId: uuidSchema,
    /** Empty for a forecast: there is no row yet, only a schedule. */
    occurrenceId: uuidSchema.nullable(),
    name: z.string().min(1),
    kind: paymentKindSchema,
    state: calendarEntryStateSchema,
    /** In the currency of the payment; empty for a variable payment nobody has confirmed. */
    amountMinor: amountMinorSchema.nullable(),
    currency: currencyCodeSchema,
    baseAmountMinor: convertedAmountMinorSchema.nullable(),
    isBaseAmountExact: z.boolean(),
});
export const calendarDaySchema = z.object({
    date: civilDateSchema,
    totalMinor: totalMinorSchema,
    isComplete: z.boolean(),
    entries: z.array(calendarEntrySchema).min(1),
});
export const calendarYearSchema = z.object({
    year: z.int(),
    baseCurrency: currencyCodeSchema,
    today: civilDateSchema,
    rateDate: civilDateSchema.nullable(),
    days: z.array(calendarDaySchema),
});
//# sourceMappingURL=summary.js.map